import { Box, Chip, MenuItem, Select, Stack, Typography } from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import type { Season, SeasonComparison, SeasonTeamDiff } from "../api/types";

function delta(now: number, before: number, digits = 0) {
    const d = Math.round((now - before) * 10 ** digits) / 10 ** digits;
    if (d === 0) return { text: "=", color: "text.secondary" };
    return { text: `${d > 0 ? "+" : ""}${d.toLocaleString("it-IT")}`, color: d > 0 ? "success.main" : "error.main" };
}

function Kpi({ label, now, before, digits = 0, suffix = "" }: { label: string; now: number; before?: number; digits?: number; suffix?: string }) {
    const d = before != null ? delta(now, before, digits) : null;
    return (
        <Box sx={{ p: 1.5, bgcolor: "white", borderRadius: 1, textAlign: "center", flex: "1 1 120px" }}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>{now.toLocaleString("it-IT")}{suffix}</Typography>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            {d && before != null && (
                <Typography variant="caption" sx={{ color: d.color }}>
                    {d.text}{d.text === "=" ? "" : suffix} (era {before.toLocaleString("it-IT")}{suffix})
                </Typography>
            )}
        </Box>
    );
}

function ListBox({ title, empty, children }: { title: string; empty: boolean; children: React.ReactNode }) {
    return (
        <Box sx={{ p: 1.5, bgcolor: "white", borderRadius: 1, flex: "1 1 260px", minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>{title}</Typography>
            {empty ? <Typography variant="body2" color="text.secondary">Nessuna</Typography> : children}
        </Box>
    );
}

const pct = (part: number, total: number) => (total ? Math.round((part / total) * 100) : 0);

interface OverallProps {
    data: SeasonComparison;
    /** stagioni precedenti selezionabili per il confronto */
    options: Season[];
    compareTo: number | null;
    onCompareToChange: (seasonId: number) => void;
}

/** Overall: confronto del torneo con una stagione precedente */
export function OverallComparison({ data, options, compareTo, onCompareToChange }: OverallProps) {
    if (!data.previous) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Nessuna stagione precedente con cui confrontare.
            </Typography>
        );
    }
    const prev = data.previous;
    const selected = compareTo ?? options.find(s => s.code === data.previousSeason)?.id ?? "";

    return (
        <Box sx={{ mb: 3, p: 2, backgroundColor: "#fff3e0", borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
                <CompareArrowsIcon sx={{ color: "#e65100" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#e65100" }}>
                    {data.season} rispetto al
                </Typography>
                <Select size="small" value={selected} onChange={e => onCompareToChange(Number(e.target.value))}
                    sx={{ bgcolor: "white", minWidth: 130 }}>
                    {options.map(s => <MenuItem key={s.id} value={s.id}>{s.code}</MenuItem>)}
                </Select>
            </Stack>

            <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
                <Kpi label="Squadre" now={data.current.teams} before={prev.teams} />
                <Kpi label="Giocatori" now={data.current.players} before={prev.players} />
                <Kpi label="Età media a inizio stagione" now={data.current.avgAge} before={prev.avgAge} digits={1} />
                <Kpi label="Donne" now={pct(data.current.women, data.current.players)} before={pct(prev.women, prev.players)} suffix="%" />
            </Stack>

            <Stack direction="row" gap={1.5} flexWrap="wrap">
                <ListBox title={`Nuove squadre (${data.newTeams.length})`} empty={!data.newTeams.length}>
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {data.newTeams.map(t => <Chip key={t} size="small" color="success" variant="outlined" label={t} />)}
                    </Stack>
                </ListBox>
                <ListBox title={`Non più presenti (${data.leftTeams.length})`} empty={!data.leftTeams.length}>
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {data.leftTeams.map(t => <Chip key={t} size="small" color="error" variant="outlined" label={t} />)}
                    </Stack>
                </ListBox>
                <ListBox title={`Cambi di nome (${data.renamed.length})`} empty={!data.renamed.length}>
                    {data.renamed.map(r => (
                        <Typography key={r.name} variant="body2">{r.previousName} → <strong>{r.name}</strong></Typography>
                    ))}
                </ListBox>
                <ListBox title={`Giocatori che hanno cambiato squadra (${data.transfers.length})`} empty={!data.transfers.length}>
                    {data.transfers.map(t => (
                        <Typography key={t.player + t.toTeam} variant="body2">
                            <strong>{t.player}</strong>: {t.fromTeam} → {t.toTeam}
                        </Typography>
                    ))}
                </ListBox>
            </Stack>
        </Box>
    );
}

/** Scheda squadra: cosa è cambiato rispetto alla stagione di confronto */
export function TeamComparison({ diff, previousSeason }: { diff?: SeasonTeamDiff; previousSeason: string | null }) {
    if (!diff || !previousSeason) return null;
    return (
        <Box sx={{ mb: 3, p: 2, backgroundColor: "#fff3e0", borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <CompareArrowsIcon fontSize="small" sx={{ color: "#e65100" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#e65100" }}>
                    Rispetto al {previousSeason}
                    {diff.previousName && diff.previousName !== diff.name && ` (come ${diff.previousName})`}
                </Typography>
            </Stack>
            {!diff.previousName ? (
                <Typography variant="body2" color="text.secondary">Squadra nuova: non era presente nel {previousSeason}.</Typography>
            ) : diff.players === 0 ? (
                <Typography variant="body2" color="text.secondary">Lista giocatori di questa stagione non ancora disponibile.</Typography>
            ) : (
                <>
                    <Stack direction="row" gap={3} flexWrap="wrap" sx={{ mb: 1.5 }}>
                        <Typography variant="body2"><strong>{diff.stayed}</strong> confermati su {diff.players}</Typography>
                        <Typography variant="body2">
                            Età media <strong>{diff.avgAge.toLocaleString("it-IT")}</strong> (era {diff.previousAvgAge.toLocaleString("it-IT")})
                        </Typography>
                        <Typography variant="body2">
                            Donne <strong>{diff.women}</strong> (erano {diff.previousWomen})
                        </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Entrati ({diff.entered.length})</Typography>
                    <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
                        {diff.entered.length ? diff.entered.map(n => <Chip key={n} size="small" color="success" variant="outlined" label={n} />)
                            : <Typography variant="body2" color="text.secondary">Nessuno</Typography>}
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Usciti ({diff.exited.length})</Typography>
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {diff.exited.length ? diff.exited.map(n => <Chip key={n} size="small" color="error" variant="outlined" label={n} />)
                            : <Typography variant="body2" color="text.secondary">Nessuno</Typography>}
                    </Stack>
                </>
            )}
        </Box>
    );
}
